class ElxElectricHeaterPurchase extends HTMLElement {
  connectedCallback() {
    this.mediaTabs = Array.from(this.querySelectorAll('[data-elx-heater-media-tab]'));
    this.mediaPanels = Array.from(this.querySelectorAll('[data-elx-heater-media-panel]'));
    this.imageSlides = Array.from(this.querySelectorAll('[data-elx-heater-slide]'));
    this.imageButtons = Array.from(this.querySelectorAll('[data-elx-heater-image-button]'));
    this.previousButton = this.querySelector('[data-elx-heater-image-prev]');
    this.nextButton = this.querySelector('[data-elx-heater-image-next]');
    this.overviewToggle = this.querySelector('[data-elx-heater-overview-toggle]');
    this.overviewPanel = this.querySelector('[data-elx-heater-overview-panel]');
    this.quantityInput = this.querySelector('[data-elx-heater-quantity]');
    this.quantityDecrease = this.querySelector('[data-elx-heater-quantity-decrease]');
    this.quantityIncrease = this.querySelector('[data-elx-heater-quantity-increase]');
    this.addButton = this.querySelector('[data-elx-heater-add]');
    this.dynamicCheckout = this.querySelector('[data-elx-heater-dynamic-checkout]');
    this.dynamicCheckoutVariant = this.querySelector('[data-elx-heater-dynamic-checkout-variant]');
    this.status = this.querySelector('[data-elx-heater-status]');
    this.mediaColumn = this.querySelector('.elx-electric-heater__media-column');
    this.mediaTrack = this.querySelector('.elx-electric-heater__media-track');
    this.mediaPin = this.querySelector('.elx-electric-heater__media-pin');
    this.mediaFollowup = this.querySelector('.elx-electric-heater__media-followup');
    this.mediaStickyBoundary = this.querySelector('.elx-electric-heater__media-sticky-boundary');
    this.crossSell = this.querySelector('.elx-electric-heater__cross-sell');
    this.followupGrid = this.querySelector('.elx-electric-heater__followup-grid');
    this.purchaseDetails = this.querySelector('.elx-electric-heater__details-primary');
    this.service = this.querySelector('.elx-electric-heater__service');
    this.details = null;
    this.detailLinks = [];
    this.detailNavigationInitialized = false;
    this.currentImageIndex = 0;

    this.normalizeMediaFlow();
    this.mediaTabs.forEach((tab) => tab.addEventListener('click', () => this.selectMedia(tab.dataset.elxHeaterMediaTab)));
    this.imageButtons.forEach((button) => button.addEventListener('click', () => this.selectImage(Number(button.dataset.elxHeaterImageButton))));
    this.previousButton?.addEventListener('click', () => this.selectImage(this.currentImageIndex - 1));
    this.nextButton?.addEventListener('click', () => this.selectImage(this.currentImageIndex + 1));
    this.overviewToggle?.addEventListener('click', () => this.setOverviewExpanded(this.overviewToggle.getAttribute('aria-expanded') !== 'true'));
    this.quantityDecrease?.addEventListener('click', () => this.stepQuantity(-1));
    this.quantityIncrease?.addEventListener('click', () => this.stepQuantity(1));
    this.quantityInput?.addEventListener('change', () => {
      this.normalizeQuantity();
      this.syncDynamicCheckout();
    });
    this.quantityInput?.addEventListener('input', () => this.syncDynamicCheckout());
    this.addButton?.addEventListener('click', () => this.addCurrentProduct());

    this.selectMedia('images');
    this.selectImage(0);
    this.setOverviewExpanded(this.overviewToggle?.getAttribute('aria-expanded') !== 'false');
    this.syncDynamicCheckout();
    this.setupMediaPinning();
    window.requestAnimationFrame(() => this.setupDetailNavigation());
    if (document.readyState === 'loading') {
      this.onWindowLoad = () => this.setupDetailNavigation();
      window.addEventListener('load', this.onWindowLoad, { once: true });
    }
  }

  disconnectedCallback() {
    this.mediaResizeObserver?.disconnect();
    this.detailObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.visualViewport?.removeEventListener('resize', this.onResize);
    if (this.onWindowLoad) window.removeEventListener('load', this.onWindowLoad);
  }

  normalizeMediaFlow() {
    if (this.mediaTrack && this.mediaFollowup && this.mediaFollowup.parentElement !== this.mediaTrack) {
      this.mediaTrack.append(this.mediaFollowup);
    }
    if (this.purchaseDetails && this.service && this.service.parentElement !== this.purchaseDetails) {
      this.purchaseDetails.append(this.service);
    }

    const desktop = window.matchMedia('(min-width: 990px)').matches;
    if (desktop && this.crossSell && this.mediaColumn && this.mediaStickyBoundary) {
      if (this.crossSell.parentElement !== this.mediaColumn) this.mediaColumn.append(this.crossSell);
      this.dataset.elxAssociatedRelease = 'cross-sell';
    } else {
      if (this.followupGrid && this.crossSell && this.crossSell.parentElement !== this.followupGrid) {
        this.followupGrid.append(this.crossSell);
      }
      delete this.dataset.elxAssociatedRelease;
    }

    if (this.followupGrid) {
      const hasVisibleChild = Array.from(this.followupGrid.children).some((child) => !child.hidden);
      this.followupGrid.hidden = desktop && !hasVisibleChild;
    }
  }

  getDetailsContentHeight() {
    if (!this.purchaseDetails) return 0;
    const detailsBox = this.purchaseDetails.getBoundingClientRect();
    const serviceBox = this.service?.getBoundingClientRect();
    if (serviceBox && this.purchaseDetails.contains(this.service)) {
      return Math.max(0, serviceBox.bottom - detailsBox.top);
    }
    return Math.max(0, this.purchaseDetails.scrollHeight);
  }

  selectMedia(name) {
    this.mediaTabs.forEach((tab) => {
      const selected = tab.dataset.elxHeaterMediaTab === name;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });
    this.mediaPanels.forEach((panel) => {
      panel.hidden = panel.dataset.elxHeaterMediaPanel !== name;
    });
    this.syncMediaPinning?.();
  }

  selectImage(index) {
    if (!this.imageSlides.length) return;
    const normalized = ((index % this.imageSlides.length) + this.imageSlides.length) % this.imageSlides.length;
    this.currentImageIndex = normalized;
    this.imageSlides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== normalized;
    });
    const ratio = Number(this.imageSlides[normalized]?.dataset.elxHeaterImageRatio);
    if (Number.isFinite(ratio) && ratio > 0) {
      this.style.setProperty('--elx-heater-active-media-ratio', String(ratio));
    } else {
      this.style.removeProperty('--elx-heater-active-media-ratio');
    }
    this.imageButtons.forEach((button) => {
      const selected = Number(button.dataset.elxHeaterImageButton) === normalized;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-current', selected ? 'true' : 'false');
    });
    this.syncMediaPinning?.();
  }

  setOverviewExpanded(expanded) {
    if (!this.overviewToggle || !this.overviewPanel) return;
    this.overviewToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.overviewPanel.classList.toggle('is-collapsed', !expanded);
  }

  setupMediaPinning() {
    const track = this.mediaTrack;
    if (!track) return;

    const sync = () => {
      this.normalizeMediaFlow();
      if (!window.matchMedia('(min-width: 990px)').matches) {
        this.style.removeProperty('--elx-heater-header-offset');
        this.style.removeProperty('--elx-heater-media-fit-width');
        this.removeAttribute('data-elx-media-compact-sticky');
        this.removeAttribute('data-elx-media-static');
        return;
      }
      const header = document.querySelector('#header-component') || document.querySelector('header-component');
      const headerHeight = Math.max(0, Math.ceil(header?.getBoundingClientRect().height || 0));
      this.style.setProperty('--elx-heater-header-offset', `${headerHeight}px`);
      const availableHeight = window.innerHeight - headerHeight - 16;
      const activePanel = this.mediaPanels.find((panel) => !panel.hidden);
      const stage = activePanel?.querySelector('.elx-electric-heater__image-stage, .elx-electric-heater__video-frame');
      const stageBox = stage?.getBoundingClientRect();
      const panelWidth = activePanel?.getBoundingClientRect().width || 0;
      const activeRatio = Number(this.style.getPropertyValue('--elx-heater-active-media-ratio'))
        || (stageBox?.height ? stageBox.width / stageBox.height : 0);
      const stageHeight = stageBox?.height || 0;
      const trackHeight = track.getBoundingClientRect().height;
      const associatedHeight = this.dataset.elxAssociatedRelease && this.crossSell
        ? this.crossSell.getBoundingClientRect().height
        : 0;
      const detailsContentHeight = this.getDetailsContentHeight();
      const fixedTrackHeight = Math.max(0, trackHeight - stageHeight);
      const naturalStageHeight = panelWidth > 0 && activeRatio > 0 ? panelWidth / activeRatio : stageHeight;
      const naturalTrackHeight = fixedTrackHeight + naturalStageHeight;
      // Keep desktop pinning through common browser-zoom changes by compacting only the visual stage.
      const compactStageHeight = availableHeight - fixedTrackHeight;
      const compactScale = naturalStageHeight > 0 ? compactStageHeight / naturalStageHeight : 0;
      const minimumMediaScale = .70;
      const minimumStickyTravel = 96;
      const normalTravel = detailsContentHeight - associatedHeight - naturalTrackHeight;
      const compactTrackHeight = fixedTrackHeight + compactStageHeight;
      const compactTravel = detailsContentHeight - associatedHeight - compactTrackHeight;
      const normalSticky = naturalTrackHeight <= availableHeight
        && normalTravel >= minimumStickyTravel;
      const compactSticky = !normalSticky
        && compactStageHeight > 0
        && compactScale >= minimumMediaScale
        && compactTravel >= minimumStickyTravel;

      if (compactSticky) {
        const fitWidth = `${Math.round(compactStageHeight * activeRatio * 100) / 100}px`;
        this.style.setProperty('--elx-heater-media-fit-width', fitWidth);
        this.setAttribute('data-elx-media-compact-sticky', '');
      } else {
        this.style.removeProperty('--elx-heater-media-fit-width');
        this.removeAttribute('data-elx-media-compact-sticky');
      }
      this.toggleAttribute('data-elx-media-static', !normalSticky && !compactSticky);
    };

    this.onResize = sync;
    sync();
    window.addEventListener('resize', this.onResize);
    this.visualViewport = window.visualViewport;
    this.visualViewport?.addEventListener('resize', this.onResize);
    if ('ResizeObserver' in window) {
      this.mediaResizeObserver?.disconnect();
      this.mediaResizeObserver = new ResizeObserver(sync);
      this.mediaResizeObserver.observe(track);
      if (this.crossSell) this.mediaResizeObserver.observe(this.crossSell);
      if (this.purchaseDetails) this.mediaResizeObserver.observe(this.purchaseDetails);
      if (this.service) this.mediaResizeObserver.observe(this.service);
    }
  }

  setupDetailNavigation() {
    if (this.detailNavigationInitialized || !('IntersectionObserver' in window)) return;
    this.details = document.querySelector('[data-elx-heater-details]');
    this.detailLinks = Array.from(this.details?.querySelectorAll('[data-elx-heater-details-link]') || []);
    if (!this.details || !this.detailLinks.length) return;
    const sections = this.detailLinks
      .map((link) => document.querySelector(link.getAttribute('href')))
      .filter(Boolean);
    const active = (section) => {
      this.detailLinks.forEach((link) => {
        const selected = link.getAttribute('href') === `#${section.id}`;
        link.toggleAttribute('aria-current', selected);
      });
    };
    const visible = new Map();
    this.detailObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => visible.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
      const candidate = [...visible.entries()].sort((left, right) => right[1] - left[1])[0]?.[0];
      if (candidate) active(candidate);
    }, { rootMargin: '-18% 0px -62% 0px', threshold: [0, .1, .25, .5] });
    sections.forEach((section) => this.detailObserver.observe(section));
    if (sections[0]) active(sections[0]);
    this.detailNavigationInitialized = true;
  }

  normalizeQuantity() {
    if (!this.quantityInput) return 1;
    const quantity = Math.min(99, Math.max(1, Number.parseInt(this.quantityInput.value || '1', 10) || 1));
    this.quantityInput.value = String(quantity);
    return quantity;
  }

  syncDynamicCheckout() {
    if (this.dynamicCheckoutVariant && this.dataset.variantId) {
      this.dynamicCheckoutVariant.value = this.dataset.variantId;
    }
  }

  stepQuantity(step) {
    const quantity = Math.min(99, Math.max(1, this.normalizeQuantity() + step));
    if (this.quantityInput) this.quantityInput.value = String(quantity);
    this.syncDynamicCheckout();
  }

  async addCurrentProduct() {
    const variantId = Number(this.dataset.variantId);
    if (!variantId || this.addButton?.disabled) return;
    await this.addItems([{ id: variantId, quantity: this.normalizeQuantity() }]);
  }

  async addItems(items) {
    if (!items.length) return;

    const pagePosition = this.captureMobilePagePosition();
    const cartDialog = document.querySelector('[data-testid="cart-drawer-dialog"]');
    if (cartDialog && pagePosition) {
      cartDialog.addEventListener('close', () => this.restoreMobilePagePosition(pagePosition), { once: true });
    }

    this.setBusy(true);
    try {
      const addItemsToCart = window.ElxCartActions?.addItemsToCart;
      if (typeof addItemsToCart !== 'function') throw new Error('Shopping cart is still loading. Please try again.');

      await addItemsToCart({
        items,
        sourceId: this.id || this.dataset.sectionId,
        source: 'elx-electric-heater-purchase',
        productId: this.dataset.productId || null,
      });
      this.announce('Added to cart.');
      if (!cartDialog?.open) this.restoreMobilePagePosition(pagePosition);
    } catch (error) {
      this.announce(error.message || 'Unable to add to cart.');
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(isBusy) {
    this.addButton?.toggleAttribute('disabled', isBusy);
    this.addButton?.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  captureMobilePagePosition() {
    if (!window.matchMedia('(max-width: 989px)').matches) return null;
    return { left: window.scrollX || 0, top: window.scrollY || window.pageYOffset || 0 };
  }

  restoreMobilePagePosition(position) {
    if (!position || !window.matchMedia('(max-width: 989px)').matches) return;
    const restore = () => window.scrollTo({ left: position.left, top: position.top, behavior: 'auto' });
    restore();
    window.requestAnimationFrame(() => {
      restore();
      window.requestAnimationFrame(restore);
    });
    window.setTimeout(restore, 160);
  }
}

if (!customElements.get('elx-electric-heater')) {
  customElements.define('elx-electric-heater', ElxElectricHeaterPurchase);
}

